import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadCopilotFile } from 'shared/api/copilot';
import { upsertCopilotCsvFileInCache } from 'shared/copilot/api';
import { findLatestStandaloneAttachedCsvUri, resolveSessionRestoreFilename, type CsvValidationReason, validateCsvFile } from 'shared/copilot/utils/csvAttachment';
import logger from 'shared/logger';
const log = logger().child({
  namespace: 'copilot/csvAttachment'
});
interface AgentStateWriter {
  state: unknown;
  setState: (state: Record<string, unknown>) => void;
}
export interface AttachedCsvFile {
  gcsUri: string;
  filename: string;
}
export type CsvAttachmentStatus = 'idle' | 'uploading' | 'error';
export interface UseCopilotCsvAttachmentArgs {
  agent: AgentStateWriter;
  clientId: number;
  userId: number | undefined;
}
export interface ClearAttachmentOptions {
  clearSessionStandalone?: boolean;
}
export interface SyncCsvRoutingStateOptions {
  messages?: ReadonlyArray<{
    role: string;
    content?: unknown;
    csvAttachMode?: string;
  }>;
  allowDocumentPartFallback?: boolean;
}
export interface UseCopilotCsvAttachmentResult {
  /** The file shown as a pending chip in the composer, cleared once its message is sent. */
  attachedFile: AttachedCsvFile | null;
  /** Name of the file currently being validated/uploaded (status === 'uploading'), for the chip label. */
  pendingFilename: string | null;
  status: CsvAttachmentStatus;
  errorReason: CsvValidationReason | 'uploadFailed' | null;
  isUploading: boolean;
  hasAttachment: boolean;
  /** Validates and uploads a file, replacing any currently attached/uploading file. */
  attachFile: (file: File, hasSelectedDatasource?: boolean) => Promise<void>;
  /**
   * Retries the last upload that failed after passing validation (status
   * `'error'` with `errorReason: 'uploadFailed'`). The original `File` is held
   * until the upload succeeds, exactly like the image attachment hook, so the
   * user never has to pick it again. A no-op if there is nothing to retry.
   */
  retryAttachment: (hasSelectedDatasource?: boolean) => Promise<void>;
  /** Clears the composer's pending chip after send. Leaves agent.state.attach_csv_file
   * intact — it is session-level context Flow's routing needs for follow-up turns. */
  clearAttachment: (options?: ClearAttachmentOptions) => void;
  /** Explicit user cancel before sending: clears the pending chip AND the
   * agent-side signal, since the attachment was never actually sent. */
  cancelAttachment: () => void;
  /** Restores the session-level agent signal only (no composer chip) — used when
   * hydrating a previously-sent standalone attachment from chat history. */
  restoreSessionAttachment: (file: AttachedCsvFile | null, options?: {
    hasSelectedDatasource?: boolean;
  }) => void;
  /** Keeps agent.state aligned when datasource selection toggles (auto-switch). */
  syncCsvRoutingState: (hasSelectedDatasource: boolean, options?: SyncCsvRoutingStateOptions) => void;
  /** Clears per-turn uploaded_csv_files after a supplementary send. */
  clearPerTurnUploadedCsvFiles: () => void;
}

/**
 * Owns Copilot CSV-attachment React state: client-side validation, upload via
 * the BFF proxy, the composer's pending-chip UI, and writing agent.state CSV
 * signals (`attach_csv_file` for standalone, `uploaded_csv_files` per-turn for
 * supplementary when a platform datasource is active).
 */
export const useCopilotCsvAttachment = t0 => {
  const $ = _c(44);
  const {
    agent,
    clientId,
    userId
  } = t0;
  const queryClient = useQueryClient();
  const [attachedFile, setAttachedFile] = useState(null);
  const [pendingFilename, setPendingFilename] = useState(null);
  const [status, setStatus] = useState("idle");
  const [errorReason, setErrorReason] = useState(null);
  const attemptTokenRef = useRef(0);
  const attachedFileRef = useRef(null);
  const pendingFileRef = useRef(null);
  const sessionStandaloneAttachRef = useRef(null);
  let t1;
  if ($[0] !== agent) {
    t1 = () => agent.state != null && typeof agent.state === "object" ? {
      ...(agent.state as Record<string, unknown>)
    } : {};
    $[0] = agent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const readCurrentAgentState = t1;
  let t2;
  if ($[2] !== agent || $[3] !== readCurrentAgentState) {
    t2 = file => {
      agent.setState({
        ...readCurrentAgentState(),
        attach_csv_file: file == null ? null : {
          gcs_uri: file.gcsUri,
          filename: file.filename
        }
      });
    };
    $[2] = agent;
    $[3] = readCurrentAgentState;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const syncAgentAttachCsvState = t2;
  let t3;
  if ($[5] !== agent || $[6] !== readCurrentAgentState) {
    t3 = () => {
      agent.setState({
        ...readCurrentAgentState(),
        uploaded_csv_files: []
      });
    };
    $[5] = agent;
    $[6] = readCurrentAgentState;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const clearPerTurnUploadedCsvFiles = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = options => {
      const composerFile = attachedFileRef.current;
      if (composerFile != null) {
        return composerFile;
      }
      const sessionFile = sessionStandaloneAttachRef.current;
      if (sessionFile != null) {
        return sessionFile;
      }
      if (options?.messages == null) {
        return null;
      }
      const latestGcsUri = findLatestStandaloneAttachedCsvUri(options.messages, {
        allowDocumentPartFallback: options.allowDocumentPartFallback
      });
      if (latestGcsUri == null) {
        return null;
      }
      const fromHistory = {
        gcsUri: latestGcsUri,
        filename: resolveSessionRestoreFilename(latestGcsUri)
      };
      sessionStandaloneAttachRef.current = fromHistory;
      return fromHistory;
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const resolveStandaloneAttachForRouting = t4;
  let t5;
  if ($[9] !== agent || $[10] !== readCurrentAgentState) {
    t5 = (hasSelectedDatasource, options_0) => {
      const currentState = readCurrentAgentState();
      if (hasSelectedDatasource) {
        agent.setState({
          ...currentState,
          attach_csv_file: null,
          uploaded_csv_files: []
        });
        return;
      }
      const file_0 = resolveStandaloneAttachForRouting(options_0);
      if (file_0 != null) {
        sessionStandaloneAttachRef.current = file_0;
        agent.setState({
          ...currentState,
          attach_csv_file: {
            gcs_uri: file_0.gcsUri,
            filename: file_0.filename
          },
          uploaded_csv_files: []
        });
      }
    };
    $[9] = agent;
    $[10] = readCurrentAgentState;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const syncCsvRoutingState = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = options_1 => {
      attemptTokenRef.current = attemptTokenRef.current + 1;
      attachedFileRef.current = null;
      pendingFileRef.current = null;
      setAttachedFile(null);
      setPendingFilename(null);
      setStatus("idle");
      setErrorReason(null);
      if (options_1?.clearSessionStandalone === true) {
        sessionStandaloneAttachRef.current = null;
      }
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const clearAttachment = t6;
  let t7;
  if ($[13] !== clearPerTurnUploadedCsvFiles || $[14] !== syncAgentAttachCsvState) {
    t7 = () => {
      clearAttachment();
      sessionStandaloneAttachRef.current = null;
      syncAgentAttachCsvState(null);
      clearPerTurnUploadedCsvFiles();
    };
    $[13] = clearPerTurnUploadedCsvFiles;
    $[14] = syncAgentAttachCsvState;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const cancelAttachment = t7;
  let t8;
  if ($[16] !== syncAgentAttachCsvState) {
    t8 = (file_1, options_2) => {
      if (options_2?.hasSelectedDatasource === true) {
        return;
      }
      if (file_1 != null) {
        sessionStandaloneAttachRef.current = file_1;
      }
      syncAgentAttachCsvState(file_1);
    };
    $[16] = syncAgentAttachCsvState;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const restoreSessionAttachment = t8;
  let t9;
  if ($[18] !== agent || $[19] !== clientId || $[20] !== queryClient || $[21] !== readCurrentAgentState || $[22] !== syncAgentAttachCsvState || $[23] !== userId) {
    t9 = async (file_2, hasSelectedDatasource_0, attemptToken) => {
      ;
      try {
        const uploaded = await uploadCopilotFile(clientId, file_2);
        if (attemptTokenRef.current !== attemptToken) {
          return;
        }
        upsertCopilotCsvFileInCache(queryClient, clientId, userId, uploaded);
        const nextFile = {
          gcsUri: uploaded.gcsUri,
          filename: uploaded.filename
        };
        attachedFileRef.current = nextFile;
        pendingFileRef.current = null;
        setAttachedFile(nextFile);
        setPendingFilename(null);
        setStatus("idle");
        if (!hasSelectedDatasource_0) {
          sessionStandaloneAttachRef.current = nextFile;
          syncAgentAttachCsvState(nextFile);
        } else {
          agent.setState({
            ...readCurrentAgentState(),
            attach_csv_file: null,
            uploaded_csv_files: []
          });
        }
      } catch (t10) {
        const error = t10;
        if (attemptTokenRef.current !== attemptToken) {
          return;
        }
        log.warn({
          error: error instanceof Error ? error.message : String(error),
          clientId
        }, "failed to upload copilot csv attachment");
        pendingFileRef.current = file_2;
        setStatus("error");
        setErrorReason("uploadFailed");
      }
    };
    $[18] = agent;
    $[19] = clientId;
    $[20] = queryClient;
    $[21] = readCurrentAgentState;
    $[22] = syncAgentAttachCsvState;
    $[23] = userId;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const performUpload = t9;
  let t10;
  if ($[25] !== performUpload) {
    t10 = async (file_3, hasSelectedDatasource_1) => {
      const attemptToken_0 = attemptTokenRef.current = attemptTokenRef.current + 1;
      attachedFileRef.current = null;
      pendingFileRef.current = null;
      setAttachedFile(null);
      setErrorReason(null);
      setPendingFilename(file_3.name);
      setStatus("uploading");
      const validation = await validateCsvFile(file_3);
      if (attemptTokenRef.current !== attemptToken_0) {
        return;
      }
      if (!validation.ok) {
        setPendingFilename(null);
        setStatus("idle");
        setErrorReason(validation.reason);
        return;
      }
      await performUpload(file_3, hasSelectedDatasource_1, attemptToken_0);
    };
    $[25] = performUpload;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const uploadAndAttachFile = t10;
  let t11;
  if ($[27] !== uploadAndAttachFile) {
    t11 = async (file_4, t12) => {
      const hasSelectedDatasource_2 = t12 === undefined ? false : t12;
      await uploadAndAttachFile(file_4, hasSelectedDatasource_2);
    };
    $[27] = uploadAndAttachFile;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const attachFile = t11;
  let t12;
  if ($[29] !== performUpload) {
    t12 = async t13 => {
      const hasSelectedDatasource_3 = t13 === undefined ? false : t13;
      const file_5 = pendingFileRef.current;
      if (file_5 == null) {
        return;
      }
      const attemptToken_1 = attemptTokenRef.current = attemptTokenRef.current + 1;
      setStatus("uploading");
      setErrorReason(null);
      await performUpload(file_5, hasSelectedDatasource_3, attemptToken_1);
    };
    $[29] = performUpload;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const retryAttachment = t12;
  const t13 = status === "uploading";
  const t14 = attachedFile != null;
  let t15;
  if ($[31] !== attachFile || $[32] !== attachedFile || $[33] !== cancelAttachment || $[34] !== clearPerTurnUploadedCsvFiles || $[35] !== errorReason || $[36] !== pendingFilename || $[37] !== restoreSessionAttachment || $[38] !== retryAttachment || $[39] !== status || $[40] !== syncCsvRoutingState || $[41] !== t13 || $[42] !== t14) {
    t15 = {
      attachedFile,
      pendingFilename,
      status,
      errorReason,
      isUploading: t13,
      hasAttachment: t14,
      attachFile,
      retryAttachment,
      clearAttachment,
      cancelAttachment,
      restoreSessionAttachment,
      syncCsvRoutingState,
      clearPerTurnUploadedCsvFiles
    };
    $[31] = attachFile;
    $[32] = attachedFile;
    $[33] = cancelAttachment;
    $[34] = clearPerTurnUploadedCsvFiles;
    $[35] = errorReason;
    $[36] = pendingFilename;
    $[37] = restoreSessionAttachment;
    $[38] = retryAttachment;
    $[39] = status;
    $[40] = syncCsvRoutingState;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
};